import { cachePolicy } from '@pn/core/cachePolicy';
import {
  formatDataType,
  type DataItem,
  type Mapping,
} from '@pn/core/domain/data';
import {
  getVisualizableQuery,
  type StreamableQuery,
} from '@pn/core/domain/query';
import {
  isNonStreamableItem,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { handleError } from '@pn/core/errors/handleError';
import { STREAMING_LIMIT } from '@pn/core/limits';
import { dynamicDataProvider } from '@pn/core/services/data/dynamicDataProvider';
import { workspaceActions } from '@pn/core/storage';
import { isCacheStale } from '@pn/core/utils/cache';
import { log } from '@pn/core/utils/debug';
import { hashSortedObject } from '@pn/services/utils/hash';
import { isNil } from 'lodash-es';
import {
  removePersistentStreamingNotification,
  showPersistentStreamingNotification,
} from './streamingNotification';

export type MapData = {
  data: DataItem[];
  timestamp: number;
  error?: string;
};

const mapDataCache = new Map<number, MapData>();

export async function getMapData(params: {
  item: WorkspaceItem;
  mapping: Mapping;
  silent?: boolean;
  rethrow?: boolean;
}): Promise<MapData> {
  const { item, mapping, silent = false, rethrow = false } = params;

  if (item.dataSource.type === 'none') {
    return {
      data: [],
      timestamp: 0,
    };
  }

  const mapQuery = getVisualizableQuery(item);

  const hash = getMapQueryHash(mapQuery);
  if (cachePolicy.mapData && mapDataCache.has(hash)) {
    const cacheCandidate = mapDataCache.get(hash)!;
    if (isCacheStale(cacheCandidate.timestamp)) {
      log(item.id).info('%cmapDataCache invalidated', 'color: #009688', hash);
      mapDataCache.delete(hash);
    } else {
      log(item.id).info('%cmapDataCache hit', 'color: #009688', hash);
      return cacheCandidate;
    }
  }

  const mapData: MapData = {
    data: [],
    timestamp: 0,
  };

  let notificationId: string | undefined;

  if (isNonStreamableItem(item)) {
    log(item.id).info('mapData early exit: non-streamable item');
    /**
     * Do not set the cache for non-streamable items. Their map comes from
     * vector tiles, so this empty result would be served to any streamable
     * item with the same query.
     */
    return { ...mapData, timestamp: Date.now() };
  }

  try {
    /**
     * Display notification if we are actually streaming data.
     */
    if (!silent) {
      notificationId = showPersistentStreamingNotification({
        action: 'Streaming',
        dataType: item.dataType,
      });
    }

    log(item.id).info('%cfetching mapData from data source', 'color: #009688');
    const { data, totalCount, error } = await dynamicDataProvider(
      item.dataSource
    ).streamDataByQuery({ query: mapQuery, mapping });

    mapData.data = data;

    const hasExceededStreamingLimit = totalCount > STREAMING_LIMIT;
    if (hasExceededStreamingLimit) {
      log(item.id).info(
        'stream request has exceeded streaming limit of',
        STREAMING_LIMIT.toLocaleString()
      );
      mapData.error = error;
    }

    if (cachePolicy.mapData) {
      mapData.timestamp = Date.now();
      mapDataCache.set(hash, mapData);
    }
  } catch (error) {
    if (rethrow) throw error;

    mapData.error = error instanceof Error ? error.message : String(error);
    workspaceActions().markAsError(item.id, mapData.error);

    handleError({
      error,
      notify: `Failed to stream ${formatDataType(item.dataType)} map data`,
    });
  } finally {
    removePersistentStreamingNotification(notificationId);
  }

  return mapData;
}

function getMapQueryHash(mapQuery: StreamableQuery): number {
  return hashSortedObject(mapQuery);
}

/**
 * This setup allows to store and return the same mapData for all workspace
 * items with identical visualizable queries.
 * This would not be possible if we used `item.id` instead of a query hash.
 */
function getMapDataByItem(item: WorkspaceItem): MapData | undefined {
  const mapQuery = getVisualizableQuery(item);
  const hash = getMapQueryHash(mapQuery);

  return mapDataCache.get(hash);
}

export function getMapDataItems(item: WorkspaceItem): DataItem[] {
  const mapData = getMapDataByItem(item);
  return !isNil(mapData) ? mapData.data : [];
}

export function clearMapDataByItem(item: WorkspaceItem): void {
  const mapQuery = getVisualizableQuery(item);
  const hash = getMapQueryHash(mapQuery);

  mapDataCache.delete(hash);
}
